import {
  applicationFormFields,
  jobValuesOf as jobToFormValues,
  toCollectionTiming,
  errorWords,
} from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc, orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { JobBuilder } from "./-components/job-builder";
/* From its own module: validateSearch is not split out with the component, and
   through job-builder.tsx it put the builder in the router's entry chunk. */
import { jobBuilderSearch } from "./-components/job-builder-search";
import { statusLabel } from "./-components/job-labels";
import { toJobAppSections } from "@/routes/_authenticated/_workforce/hiring/-components/job-app-sections-editor";
import { parseScreeningQuestions, toUpdateFields, type JobDraft } from "./-components/job-form";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/jobs/$jobId")({
  validateSearch: jobBuilderSearch,
  component: EditJobPage,
});

/** An existing job posting, opened to change one thing: the advert, the questions, the stages a
 * candidate walks, or the paperwork at the end. */
function EditJobPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const { step } = Route.useSearch();
  const queryClient = useQueryClient();
  const canManage = useCan("hiring:manage");

  const job = useQuery(orpc.workforce.hiring.getJob.queryOptions({ input: { jobId } }));
  const documents = useQuery(
    orpc.workforce.hiringCatalog.jobDocuments.queryOptions({ input: { jobId } }),
  );
  const forms = useQuery(
    orpc.workforce.hiringCatalog.jobFormTemplates.queryOptions({ input: { jobId } }),
  );
  const screening = useQuery(
    orpc.workforce.hiringTemplates.screening.forJob.queryOptions({ input: { jobId } }),
  );

  const save = useMutation({
    mutationFn: async (draft: JobDraft) => {
      await orpcClient.workforce.hiring.updateJob({
        jobId,
        ...toUpdateFields(draft.values),
        screeningQuestions: draft.screeningQuestions,
        ...applicationFormFields(draft),
      });
      await Promise.all([
        orpcClient.workforce.hiringCatalog.setJobFormTemplates({
          jobId,
          formTemplateIds: draft.formTemplateIds,
        }),
        orpcClient.workforce.hiringCatalog.setJobDocuments({
          jobId,
          assignments: draft.documents,
        }),
        orpcClient.workforce.hiringTemplates.screening.setForJob({
          jobId,
          screeningTemplateId: draft.screeningTemplateId,
        }),
      ]);
    },
    onSuccess: async () => {
      // Stays in the posting: being sent to the list after every save meant
      // finding the job again to carry on.
      toast.success("Job saved");
      await queryClient.invalidateQueries({ queryKey: orpc.workforce.key() });
    },
    onError: (error: Error) => toast.error(errorWords(error)),
  });

  if (job.isPending || documents.isPending || forms.isPending || screening.isPending) {
    return (
      <>
        <PageHeader title="Job posting" />
        <Skeleton className="h-96 w-full" />
      </>
    );
  }

  if (job.isError) {
    return (
      <>
        <PageHeader title="Job posting" />
        <ReadFailed
          message="Could not load this posting."
          error={job.error}
          onRetry={() => void job.refetch()}
          retrying={job.isFetching}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={job.data.title}
        meta="What the job is, what applicants are asked, and what they sign."
        actions={
          <Badge variant={job.data.status === "active" ? "ok" : "outline"}>
            {statusLabel(job.data.status)}
          </Badge>
        }
      />
      {canManage ? null : (
        <p className="text-muted-foreground text-sm">
          You can read this posting. Changing it needs hiring access.
        </p>
      )}
      <JobBuilder
        key={job.data.id}
        mode="edit"
        pending={save.isPending}
        readOnly={!canManage}
        jobId={jobId}
        initial={{
          values: jobToFormValues(job.data),
          screeningQuestions: parseScreeningQuestions(job.data.screeningQuestions),
          screeningTemplateId: screening.data?.screeningTemplateId ?? null,
          jobAppSections: toJobAppSections(job.data.jobAppSections),
          jobAppTemplateId: job.data.jobAppTemplateId,
          formTemplateIds: (forms.data ?? []).map((row) => row.formTemplateId),
          documents: (documents.data ?? []).map((row) => ({
            documentTypeId: row.documentTypeId,
            label: row.label,
            isRequired: row.isRequired,
            collectionTiming: toCollectionTiming(row.collectionTiming),
          })),
        }}
        step={step}
        onStepChange={(next) => void navigate({ to: ".", search: { step: next }, replace: true })}
        onSubmit={(draft) => save.mutate(draft)}
      />
    </>
  );
}
